"use client";

import { track } from "@opensea/analytics";
import { useChains } from "@opensea/app-kit";
import { useRouter } from "@opensea/navigation";
import { CenterAligned, classNames, EmptyState, Flex, FlexColumn, TextLabel } from "@opensea/ui-kit";
import { useDebounceValue, useOnChange } from "@opensea/ui-kit/hooks";
import { Search } from "@opensea/ui-kit/icons";
import { range } from "es-toolkit/math";
import { useTranslations } from "next-intl";
import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { ExpandedSearchResult } from "@/components/expanded-search/results/ExpandedSearchResult";
import { ExpandedSearchChainFilter } from "./ExpandedSearchChainFilter";
import { ExpandedSearchMenuPart } from "./ExpandedSearchMenuPart";
import { ExpandedSearchPage } from "./ExpandedSearchPage";
import { ExpandedSearchResultSections } from "./ExpandedSearchResultSections";
import { ExpandedSearchSection } from "./ExpandedSearchSection";
import { useExpandedSearchStore } from "./ExpandedSearchStore";
import { ExpandedSearchTrendingCollectionCard } from "./ExpandedSearchTrendingCollectionCard";
import { ExpandedSearchTrendingCurrencyCard } from "./ExpandedSearchTrendingCurrencyCard";
import { EXPANDED_SEARCH_COLLECTIONS_ENHANCED_LIMIT, EXPANDED_SEARCH_COLLECTIONS_LIMIT, EXPANDED_SEARCH_CURRENCIES_ENHANCED_LIMIT, EXPANDED_SEARCH_CURRENCIES_LIMIT, useExpandedSearchQueryResults, useExpandedSearchVisibleItems } from "./hooks";
import { mapAccountToSearchResultItem } from "./results/ExpandedSearchAccountResult";
import { ExpandedSearchCollectionResultSkeleton, mapCollectionToSearchResultItem } from "./results/ExpandedSearchCollectionResult";
import { ExpandedSearchCurrencyResultSkeleton, mapCurrencyToSearchResultItem } from "./results/ExpandedSearchCurrencyResult";
import { mapItemToSearchResultItem } from "./results/ExpandedSearchItemResult";
import { useGetHrefForExpandedSearchResultItem } from "./results/utils";
const DEBOUNCE_MS = 200;
export function ExpandedSearchMenuResults({
  query,
  setQuery,
  selectionMethod,
  selectedCategory,
  selectedChain,
  onSelectChainAction,
  onPointerMove,
  trendingCollections,
  topCurrencies,
  expandedSearchMenuResultsRef
}) {
  const t = useTranslations("ExpandedSearchMenuResults");
  const router = useRouter();
  const {
    isChainCurrencyOnly
  } = useChains();
  const [open, setOpen, setLoading, pushRecentSearch, clearRecentSearches] = useExpandedSearchStore(useShallow(state => [state.open, state.setOpen, state.setLoading, state.pushRecentSearch, state.clearRecentSearches]));
  const isQueryNotEmpty = query.trim().length > 0;
  const [debouncedQuery] = useDebounceValue(query, DEBOUNCE_MS);
  const defaultListRef = useRef(null);
  const resultsListRef = useRef(null);

  // Scroll to top when search query changes
  useEffect(() => {
    defaultListRef.current?.scrollTo(0, 0);
    resultsListRef.current?.scrollTo(0, 0);
  }, [debouncedQuery]);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const {
    searchResults,
    sections,
    accounts,
    isAccountsLoading,
    collections,
    isCollectionsLoading,
    items,
    isItemsLoading,
    currencies,
    isCurrenciesLoading,
    recentSearches,
    errors: searchErrors,
    fetching: searchFetching
  } = useExpandedSearchQueryResults({
    query,
    debouncedQuery,
    selectedCategory,
    selectedChain
  });
  const [debouncedFetching] = useDebounceValue(searchFetching, DEBOUNCE_MS);
  const isFetchPending =
  // A network request to fetch results is due
  query.trim().length > 0 && debouncedQuery.trim().length > 0 && query.trim() !== debouncedQuery.trim() ||
  // We need to submit the initial network request
  query.trim().length > 0 && debouncedQuery.trim().length === 0;
  const fetching =
  // We're making a network request or
  (query.length > 0 || searchResults.length > 0 ? searchFetching : debouncedFetching) || isFetchPending;
  useOnChange(fetching, () => {
    setLoading(fetching);
  });
  const {
    visibleItems
  } = useExpandedSearchVisibleItems({
    searchResults,
    isQueryNotEmpty,
    recentSearches,
    trendingCollections,
    topCurrencies,
    selectedCategory,
    selectedChain
  });
  const getHref = useGetHrefForExpandedSearchResultItem();
  const redirectToItem = (item, {
    openInNewTab = false
  } = {
    openInNewTab: false
  }) => {
    const href = getHref(item);
    if (!href) {
      return;
    }
    if (openInNewTab) {
      window.open(href, "_blank");
      return;
    }
    router.push(href);
    setOpen(false);
  };
  const navigateToSelectedItem = (index = selectedItemIndex, navigate = true, navigateOptions) => {
    const selectedItem = visibleItems.at(index);
    if (!selectedItem) {
      return;
    }
    switch (selectedItem.type) {
      case "recent-search":
        // Track recent search selection
        void track("Recent Search Selected", {
          recent_search_type: selectedItem.data.type,
          category: selectedCategory
        });
        switch (selectedItem.data.type) {
          case "query":
            setQuery(selectedItem.data.data.query);
            pushRecentSearch({
              key: selectedItem.data.key,
              data: selectedItem.data.data,
              type: selectedItem.data.type
            });
            break;
          case "account":
          case "item":
          case "collection":
          case "currency":
            pushRecentSearch({
              key: selectedItem.data.key,
              data: selectedItem.data.data,
              type: selectedItem.data.type
            });
            if (navigate) {
              redirectToItem({
                ...selectedItem.data,
                id: selectedItem.id
              }, navigateOptions);
            } else {
              setOpen(false);
            }
            break;
          default:
            break;
        }
        break;
      case "account":
      case "item":
      case "collection":
      case "currency":
        // Track search result click
        void track("Search Result Clicked", {
          result_type: selectedItem.type,
          result_id: selectedItem.id,
          result_position: index,
          query,
          category: selectedCategory
        });
        pushRecentSearch({
          key: selectedItem.id,
          data: selectedItem.data,
          type: selectedItem.type
        });
        if (navigate) {
          redirectToItem(selectedItem, navigateOptions);
        } else {
          setOpen(false);
        }
        break;
      case "link":
        // Track link click (usually "View all" links)
        void track("Search Result Clicked", {
          result_type: "link",
          category: selectedCategory,
          query
        });
        if (navigate) {
          redirectToItem(selectedItem, navigateOptions);
        } else {
          setOpen(false);
        }
        break;
    }
  };
  const renderResult = (item_0, resultsRef) => {
    const index_0 = visibleItems.findIndex(visibleItem => visibleItem.id === item_0.id);
    return <ExpandedSearchResult item={item_0} key={item_0.id} onClick={() => {
      navigateToSelectedItem(index_0, false);
    }} onHover={() => {
      if (selectionMethod === "mouse") {
        setSelectedItemIndex(index_0);
      }
    }} onSelect={() => {
      navigateToSelectedItem(index_0);
    }} resultsListRef={resultsRef} selected={index_0 === selectedItemIndex} selectedItemIndex={selectedItemIndex} selectionMethod={selectionMethod} />;
  };
  useImperativeHandle(expandedSearchMenuResultsRef, () => ({
    navigate: options => {
      navigateToSelectedItem(undefined, undefined, options);
    },
    previous: () => {
      if (visibleItems.length === 0) {
        return;
      }
      const newIndex = selectedItemIndex - 1;
      setSelectedItemIndex(newIndex >= 0 ? newIndex : visibleItems.length - 1);
      tryPushQueryToRecentSearches();
    },
    forward: () => {
      if (visibleItems.length === 0) {
        return;
      }
      const newIndex_0 = selectedItemIndex + 1;
      setSelectedItemIndex(newIndex_0 < visibleItems.length ? newIndex_0 : 0);
      tryPushQueryToRecentSearches();
    },
    setSelectedItemIndex
  }));

  /**
   * Recent Searches
   */
  const hasPushedQueryToRecentSearchesRef = useRef(false);
  const tryPushQueryToRecentSearches = () => {
    if (query.trim().length === 0 || hasPushedQueryToRecentSearchesRef.current) {
      return;
    }
    pushRecentSearch({
      key: query,
      data: {
        query
      },
      type: "query"
    });
    hasPushedQueryToRecentSearchesRef.current = true;
  };

  // Reset scroll position and query on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setSelectedItemIndex(0);
      defaultListRef.current?.scrollTo(0, 0);
      resultsListRef.current?.scrollTo(0, 0);
    }
  }, [open]);
  const sectionsData = {
    collection: {
      items: collections.map(mapCollectionToSearchResultItem),
      isLoading: isCollectionsLoading || isFetchPending
    },
    currency: {
      items: currencies.map(mapCurrencyToSearchResultItem),
      isLoading: isCurrenciesLoading || isFetchPending
    },
    item: {
      items: items.map(mapItemToSearchResultItem),
      isLoading: isItemsLoading || isFetchPending
    },
    account: {
      items: accounts.map(mapAccountToSearchResultItem),
      isLoading: isAccountsLoading || isFetchPending
    }
  };
  function getBaseSections() {
    switch (selectedCategory) {
      case "collections":
        return sections.filter(section_2 => section_2 === "collection");
      case "items":
        return sections.filter(section_1 => section_1 === "item");
      case "tokens":
        return sections.filter(section_0 => section_0 === "currency");
      case "wallets":
        return sections.filter(section => section === "account");
      default:
        return sections;
    }
  }
  function getFilteredSections() {
    const baseSections = getBaseSections();

    // For currency-only chains (like Solana), remove NFT sections
    if (selectedChain !== "all" && isChainCurrencyOnly(selectedChain)) {
      return baseSections.filter(section_3 => section_3 !== "collection" && section_3 !== "item");
    }
    return baseSections;
  }
  function renderRecentSearchSection() {
    {
      // Use the already-filtered recent searches from visibleItems (which includes chain filtering)
      const recentSearchItems = visibleItems.filter(item_1 => item_1.type === "recent-search");
      return recentSearchItems.length > 0 && <ExpandedSearchSection action={t("clearAction")} heading={t("recentSearches")} headingClassName="mb-2" onActionClick={() => {
        clearRecentSearches();
      }}>
            {recentSearchItems.map(recentSearchItem => renderResult(recentSearchItem, defaultListRef))}
          </ExpandedSearchSection>;
    }
  }
  return <ExpandedSearchMenuPart aria-activedescendant={visibleItems.length > 0 && selectedItemIndex >= 0 ? `search-result-${selectedItemIndex}` : undefined} className="relative size-full flex-1 select-none" onPointerLeave={() => {
    if (selectionMethod === "mouse") {
      setSelectedItemIndex(-1);
    }
  }} onPointerMove={onPointerMove} role="listbox">
      <FlexColumn className="h-full w-full flex-1">
        {selectedCategory === "all" && <ExpandedSearchChainFilter category="all" chainType="all" className="border-border-1 border-b py-3" onSelectChainAction={onSelectChainAction} value={selectedChain} />}
        {selectedCategory === "tokens" && <ExpandedSearchChainFilter category="tokens" chainType="all" className="border-border-1 border-b py-3" onSelectChainAction={onSelectChainAction} value={selectedChain} />}
        {selectedCategory === "collections" && <ExpandedSearchChainFilter category="collections" chainType="evm" className="border-border-1 border-b py-3" onSelectChainAction={onSelectChainAction} value={selectedChain} />}
        {selectedCategory === "items" && <ExpandedSearchChainFilter category="items" chainType="evm" className="border-border-1 border-b py-3" onSelectChainAction={onSelectChainAction} value={selectedChain} />}
        <div className="relative flex-1">
          <ExpandedSearchPage className="p-4" direction="right" presented={query.trim().length > 0} ref={resultsListRef}>
            <ExpandedSearchResultSections renderResult={renderResult} resultsListRef={resultsListRef} sectionData={sectionsData} sections={getFilteredSections()} selectedChain={selectedChain} />
          </ExpandedSearchPage>
          <ExpandedSearchPage className="p-4" direction="center" presented={fetching ? searchErrors.length > 0 : false} ref={defaultListRef}>
            <Flex className="size-full items-center justify-center opacity-25">
              <TextLabel>{t("somethingWentWrong")}</TextLabel>
            </Flex>
          </ExpandedSearchPage>

          <ExpandedSearchPage className="p-4" direction="left" presented={!fetching && query.trim().length === 0} ref={defaultListRef}>
            {renderRecentSearchSection()}
            {trendingCollections && (selectedCategory === "all" || selectedCategory === "collections") ? <ExpandedSearchSection heading={t("trendingCollections")}>
                {trendingCollections.length > 0 ? <div className={classNames("grid grid-cols-1 gap-3 md:grid-cols-2")}>
                    {trendingCollections.map((collectionKey, index_1) => {
                const item_2 = mapCollectionToSearchResultItem(collectionKey);
                const itemIndex = visibleItems.findIndex(visibleItem_0 => visibleItem_0.id === item_2.id);
                const isSelected = itemIndex === selectedItemIndex;
                return <ExpandedSearchTrendingCollectionCard collection={collectionKey} key={`trending-collection-${index_1}`} onPointerEnter={() => {
                  if (selectionMethod === "mouse" && itemIndex >= 0) {
                    setSelectedItemIndex(itemIndex);
                  }
                }} onSelect={() => {
                  if (itemIndex >= 0) {
                    navigateToSelectedItem(itemIndex, false);
                  } else {
                    // If the item is not in visibleItems, navigate directly to the item
                    redirectToItem(item_2);
                  }
                }} selected={isSelected} />;
              })}
                  </div> : <div className={classNames("grid grid-cols-1 gap-3 md:grid-cols-2")}>
                    {range(selectedCategory === "collections" ? EXPANDED_SEARCH_COLLECTIONS_ENHANCED_LIMIT : EXPANDED_SEARCH_COLLECTIONS_LIMIT).map((_, index_2) => <ExpandedSearchCollectionResultSkeleton className="rounded-lg p-3" index={index_2} key={index_2} />)}
                  </div>}
              </ExpandedSearchSection> : undefined}
            {topCurrencies && (selectedCategory === "all" || selectedCategory === "tokens") ? <ExpandedSearchSection heading={t("topTokens")}>
                {topCurrencies.length > 0 ? <div className={classNames("grid grid-cols-1 gap-3 md:grid-cols-2")}>
                    {topCurrencies.map((currencyKey, index_3) => {
                const item_3 = mapCurrencyToSearchResultItem(currencyKey);
                const itemIndex_0 = visibleItems.findIndex(visibleItem_1 => visibleItem_1.id === item_3.id);
                const isSelected_0 = itemIndex_0 === selectedItemIndex;
                return <ExpandedSearchTrendingCurrencyCard currency={currencyKey} key={`top-currency-${index_3}`} onPointerEnter={() => {
                  if (selectionMethod === "mouse" && itemIndex_0 >= 0) {
                    setSelectedItemIndex(itemIndex_0);
                  }
                }} onSelect={() => {
                  if (itemIndex_0 >= 0) {
                    navigateToSelectedItem(itemIndex_0, false);
                  } else {
                    // If the item is not in visibleItems, navigate directly to the item
                    redirectToItem(item_3);
                  }
                }} selected={isSelected_0} />;
              })}
                  </div> : <div className={classNames("grid grid-cols-1 gap-3 md:grid-cols-2")}>
                    {range(selectedCategory === "tokens" ? EXPANDED_SEARCH_CURRENCIES_ENHANCED_LIMIT : EXPANDED_SEARCH_CURRENCIES_LIMIT).map((__0, index_4) => <ExpandedSearchCurrencyResultSkeleton className="h-16 rounded-lg p-3" index={index_4} key={index_4} />)}
                  </div>}
              </ExpandedSearchSection> : undefined}
            {selectedCategory === "wallets" && visibleItems.filter(item_4 => item_4.type === "recent-search").length === 0 && <CenterAligned className="size-full p-8">
                  <EmptyState className="text-center" description={t("walletsZeroState")} icon={Search} title={t("searchWallets")} variant="no-results" />
                </CenterAligned>}
            {selectedCategory === "items" && visibleItems.filter(item_5 => item_5.type === "recent-search").length === 0 && <CenterAligned className="size-full p-8">
                  <EmptyState className="text-center" description={t("itemsZeroState")} icon={Search} title={t("searchItems")} variant="no-results" />
                </CenterAligned>}
          </ExpandedSearchPage>
        </div>
      </FlexColumn>
    </ExpandedSearchMenuPart>;
}